/**
 * The HTTP client. Every fetch in the app goes through `request`.
 *
 * Two things it does that a bare `fetch` does not:
 *
 * **401 is an auth boundary, not a transient failure.** It throws `Unauthorized`
 * so the query layer can stop retrying and redirect, rather than retrying three
 * times against a session that is definitively gone.
 *
 * **Money is never parsed into a number.** The API sends it as a string on
 * purpose — JSON numbers are IEEE doubles — and `null` means "nothing recorded",
 * which is a different fact from zero.
 */

export class Unauthorized extends Error {}
/** The request's `Origin` is not one this deployment serves. */
export class OriginRefused extends Error {}
export class RateLimited extends Error {
  constructor(public readonly retryAfterSeconds: number) {
    super("too many attempts");
  }
}
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  if (response.status === 401) throw new Unauthorized("not signed in");
  if (response.status === 403) {
    // Distinct from 401. Conflating them is what made a deployment-configuration
    // problem read as a wrong password.
    throw new OriginRefused(await detailOf(response));
  }
  if (response.status === 429) {
    throw new RateLimited(Number(response.headers.get("Retry-After") ?? 60));
  }
  if (!response.ok) {
    throw new ApiError(response.status, (await detailOf(response)) || response.statusText);
  }
  return (await response.json()) as T;
}

/**
 * The human half of a FastAPI error body.
 *
 * Every view renders `error.message` directly, and the raw body is
 * `{"detail":"…"}` — so an operator whose trigger was refused read a JSON
 * envelope instead of the sentence inside it. On a 422 it is worse: FastAPI
 * emits an array of validation objects, and the whole array was being printed
 * under the sign-in form.
 *
 * Falls back to the raw text, because an unparseable body is still better
 * evidence than an empty string.
 */
async function detailOf(response: Response): Promise<string> {
  const body = await response.text();
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed && typeof parsed === "object" && "detail" in parsed) {
      const { detail } = parsed as { detail: unknown };
      if (typeof detail === "string") return detail;
      // Pydantic's validation array: name the field and say what is wrong with
      // it, rather than pasting `loc`/`ctx`/`url` at someone.
      if (Array.isArray(detail)) {
        return detail
          .map((item: unknown) => {
            if (!item || typeof item !== "object") return String(item);
            const { loc, msg } = item as { loc?: unknown; msg?: unknown };
            const field = Array.isArray(loc) ? loc.filter((p) => p !== "body").join(".") : "";
            return field ? `${field}: ${String(msg)}` : String(msg);
          })
          .join("; ");
      }
    }
  } catch {
    // Not JSON. The raw text is the best available detail.
  }
  return body;
}

/**
 * A unique id, in a context that may not have `crypto.randomUUID`.
 *
 * **`randomUUID` is `[SecureContext]`.** This console is served over plain
 * HTTP on an IP address — `http://192.168.2.78:18090` — which is not a secure
 * context, so `crypto` exists, `crypto.getRandomValues` exists, and
 * `crypto.randomUUID` is `undefined`. Calling it throws a `TypeError` rather
 * than failing any feature test, and called during render it took the whole
 * React tree down: the project page appeared, then went blank, tabs and all.
 *
 * `getRandomValues` is *not* secure-context gated, so the fallback is as
 * random as the real thing; only the formatting is ours. Shape follows RFC
 * 4122 v4 so the value still reads as a UUID in a log or an audit row.
 */
export function newId(): string {
  if (typeof crypto?.randomUUID === "function") return crypto.randomUUID();

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  // Mapped rather than mutated by index: `noUncheckedIndexedAccess` types
  // `bytes[6]` as possibly undefined, and the honest way past that is to not
  // index at all.
  const hex = Array.from(bytes, (b, i) => {
    if (i === 6) return ((b & 0x0f) | 0x40).toString(16).padStart(2, "0"); // version 4
    if (i === 8) return ((b & 0x3f) | 0x80).toString(16).padStart(2, "0"); // variant 10xx
    return b.toString(16).padStart(2, "0");
  }).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export interface Principal {
  email: string;
  is_admin: boolean;
  actor_type: string;
}

export interface RunSummary {
  id: string;
  repo: string;
  run_date: string;
  status: string;
  reason: string | null;
  dry_run: boolean;
  trigger: string;
  considered: number;
  selected: number;
  dispatched: number;
  /** A decimal string, or null when nothing is recorded. Never a number. */
  total_cost_usd: string | null;
  /** From the server, so the status vocabulary is not re-derived here. */
  is_terminal: boolean;
}

export interface IterationDetail {
  id: string;
  seq: number;
  mode: string;
  outcome: string | null;
  /** Null means the iteration never reported, which is not the same as zero. */
  blocking_findings: number | null;
  total_findings: number | null;
  cost_usd: string | null;
  started_at: string;
  ended_at: string | null;
}

export interface AttemptDetail {
  id: string;
  sentry_issue_id: string;
  sentry_short_id: string | null;
  title: string | null;
  status: string;
  reason: string | null;
  reason_detail: string | null;
  fixability_score: string | null;
  change_request_url: string | null;
  branch: string | null;
  forced_reason: string | null;
  is_terminal: boolean;
  cost_usd: string | null;
  iterations: IterationDetail[];
}

export interface RunDetail {
  run: RunSummary;
  cost: {
    triage: string | null;
    attempt_outside_iteration: string | null;
    per_iteration: string | null;
    total: string | null;
  };
  attempts: AttemptDetail[];
}

export interface DocumentRef {
  id: string;
  kind: string;
  agent: string;
  iteration_id: string | null;
  size_bytes: number;
  created_at: string;
}

export interface ConfidenceCap {
  check: string;
  applied: boolean;
  ceiling: string | null;
  delta: string | null;
  detail: string;
}

export interface Finding {
  id: string;
  seq: number;
  severity: string;
  category: string;
  lens: string | null;
  file_path: string | null;
  line: number | null;
  description: string;
  suggested_remedy: string | null;
  blocking: boolean;
}

export interface ServiceSummary {
  id: string;
  name: string;
  stack: string;
  sentry_org: string;
  sentry_project: string;
}

export interface ProjectSummary {
  id: string;
  repo: string;
  repo_url: string | null;
  forge_provider: string | null;
  /** The last four characters, so you can tell which credential is in place. */
  forge_token_hint: string | null;
  default_branch: string;
  enabled: boolean;
  /** What a live run here is permitted to do to the repository. */
  write_mode: string;
  nightly_budget_usd: string;
  max_issues_per_night: number;
  /** Whether, never which — the secret's name is not a read endpoint's business. */
  has_forge_token: boolean;
  services: ServiceSummary[];
}

export interface TriggerResult {
  run_id: string;
  created: boolean;
  considered: number;
  selected: number;
  requested_n: number | null;
  effective_n: number | null;
  /** Set when the request asked for more than the project permits. */
  clamped_by: string | null;
  /** How many selected attempts a backend actually accepted. `selected: 3,
   *  dispatched: 0` is a run whose triage worked and whose execution did not. */
  dispatched: number;
  /** One line per attempt the backend refused. The run is still real. */
  dispatch_errors: string[];
}

export interface TriggerRequest {
  project_id: string;
  service_name: string;
  dry_run: boolean;
  start?: string;
  end?: string;
  max_issues?: number;
  sentry_issue_id?: string;
  idempotency_key?: string;
}

export interface ServiceBody {
  name: string;
  stack: string;
  sentry_org: string;
  sentry_project: string;
  build_cmd: string;
  test_cmd: string;
  jdk: string;
  path: string;
}

export interface ProjectBody {
  /** The https clone URL. `owner/name` is derived from it, never asked for. */
  repo_url: string;
  forge_provider: string;
  /** Sent once, stored encrypted, never returned. */
  forge_token: string | null;
  default_branch: string;
  nightly_budget_usd: number;
  max_issues_per_night: number;
  per_issue_share: number;
  services: ServiceBody[];
}

export interface ProjectPatch {
  default_branch?: string;
  nightly_budget_usd?: number;
  max_issues_per_night?: number;
  per_issue_share?: number;
  enabled?: boolean;
  write_mode?: string;
  /** Replaces whatever is stored. There is no way to read the old one back. */
  forge_token?: string;
  schedule_utc?: string;
}

export interface PreflightCheck {
  name: string;
  status: "ok" | "warn" | "fail" | "skip";
  detail: string;
  remedy: string;
}

export interface AuditEntry {
  at: string;
  actor_label: string;
  actor_type: string;
  action: string;
  outcome: string;
  subject_type: string | null;
  subject_id: string | null;
  detail: string | null;
  source_ip: string | null;
}

export interface TokenSummary {
  id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface SentryIssue {
  id: string;
  short_id: string;
  title: string;
  culprit: string;
  error_type: string | null;
  count: number;
  user_count: number;
  level: string;
  first_seen: string | null;
  last_seen: string | null;
  permalink: string;
  /** What the engine already did with this issue, so the button never offers
   *  to start work that is finished or in flight. */
  last_status: string | null;
  last_reason: string | null;
  last_attempt_id: string | null;
  last_run_id: string | null;
  in_flight: boolean;
  change_request_url: string | null;
}

export interface IssueList {
  service: string;
  issues: SentryIssue[];
  /** Set when Sentry refused us. Never conflated with "no issues". */
  error: { detail: string; remedy: string } | null;
}

/** What one sweep of the reaper reclaimed. Mirrors `ReapResponse` server-side.
 *
 *  Typed rather than `Record<string, number | string[]>`: the loose shape forced
 *  a cast at every read, and a cast is exactly the thing that keeps compiling
 *  when the server's field names change. */
export interface ReapReport {
  undispatched_attempts: number;
  wedged_attempts: number;
  cancelled_attempts: number;
  stuck_runs: number;
  released_reservations: number;
  expired_sessions: number;
  errors: string[];
}

export interface SentryProjectOption {
  org: string;
  slug: string;
  name: string;
  platform: string | null;
}

export interface SentryProjectList {
  projects: SentryProjectOption[];
  /** Set when Sentry could not be asked. The form falls back to a text box,
   *  which it can only do if it can tell this from "no projects". */
  error: { detail: string; remedy: string } | null;
}

export interface ProjectRun {
  id: string;
  run_date: string;
  status: string;
  reason: string | null;
  dry_run: boolean;
  trigger: string;
  started_at: string;
  ended_at: string | null;
  considered: number;
  selected: number;
  dispatched: number;
  total_cost_usd: string | null;
  is_terminal: boolean;
}

export interface AttemptEvent {
  at: string;
  phase: string;
  label: string;
  status: string;
  iteration: number | null;
  detail: string | null;
}

export const api = {
  me: () => request<Principal>("/api/auth/me"),
  login: (email: string, password: string) =>
    request<Principal>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  projects: () => request<ProjectSummary[]>("/api/projects"),
  project: (id: string) => request<ProjectSummary>(`/api/projects/${id}`),
  projectIssues: (id: string) => request<IssueList>(`/api/projects/${id}/issues`),
  projectRuns: (id: string) => request<ProjectRun[]>(`/api/projects/${id}/runs`),
  attemptEvents: (attemptId: string) =>
    request<AttemptEvent[]>(`/api/attempts/${attemptId}/events`),
  runs: () => request<RunSummary[]>("/api/runs"),
  run: (id: string) => request<RunDetail>(`/api/runs/${id}`),
  documents: (attemptId: string) =>
    request<DocumentRef[]>(`/api/attempts/${attemptId}/documents`),
  confidence: (attemptId: string) =>
    request<ConfidenceCap[]>(`/api/attempts/${attemptId}/confidence`),
  findings: (attemptId: string) => request<Finding[]>(`/api/attempts/${attemptId}/findings`),
  trigger: (body: TriggerRequest) =>
    request<TriggerResult>("/api/runs", { method: "POST", body: JSON.stringify(body) }),

  options: () =>
    request<{
      stacks: string[];
      providers: string[];
      forge_hosts: string[];
      /** Which Sentry installation the project slug will be looked up in. */
      sentry_base_url: string;
    }>("/api/settings/options"),
  sentryProjects: () => request<SentryProjectList>("/api/settings/sentry-projects"),
  registerProject: (body: ProjectBody) =>
    request<ProjectSummary>("/api/projects", { method: "POST", body: JSON.stringify(body) }),
  updateProject: (id: string, patch: ProjectPatch) =>
    request<ProjectSummary>(`/api/projects/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  addService: (id: string, body: ServiceBody) =>
    request<ProjectSummary>(`/api/projects/${id}/services`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // A POST, because it writes a storage probe and asks the provider about the
  // key. As a GET it was exempt from the Origin check, which is how a
  // cross-origin page could spend on this deployment's behalf.
  preflight: (projectId?: string) =>
    request<{ ready: boolean; checks: PreflightCheck[] }>(
      projectId ? `/api/preflight?project_id=${projectId}` : "/api/preflight",
      { method: "POST" },
    ),
  reap: () => request<ReapReport>("/api/maintenance/reap", { method: "POST" }),
  cancelAttempt: (id: string) =>
    request<{ status: string; reason: string; note: string }>(`/api/attempts/${id}/cancel`, {
      method: "POST",
    }),
  audit: (limit = 100) => request<AuditEntry[]>(`/api/audit?limit=${limit}`),
  tokens: () => request<TokenSummary[]>("/api/tokens"),
  createToken: (name: string) =>
    request<{ token: string; note: string }>("/api/tokens", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  revokeToken: (id: string) =>
    request<{ revoked: boolean }>(`/api/tokens/${id}`, { method: "DELETE" }),
};

/**
 * Fetch an artifact's text.
 *
 * Verified against the length the server promised: a truncated stream arrives
 * as a 200, and a diagnosis cut off at 40% reads as a diagnosis that stopped
 * thinking rather than as a transfer that failed.
 */
export async function fetchDocument(id: string, expectedBytes: number): Promise<string> {
  const response = await fetch(`/api/documents/${id}`, { headers: { Accept: "text/plain" } });
  if (response.status === 401) throw new Unauthorized("not signed in");
  if (response.status === 413) throw new ApiError(413, "artifact is too large to display");
  if (!response.ok) throw new ApiError(response.status, response.statusText);

  const text = await response.text();
  const received = new TextEncoder().encode(text).length;
  if (received < expectedBytes) {
    throw new ApiError(
      200,
      `truncated: received ${received} of ${expectedBytes} bytes`,
    );
  }
  return text;
}

/**
 * A decimal string as currency, or an em dash. Never `$0.00` for absent.
 *
 * **Formatted from the string, never through a number.** This was
 * `parseFloat(value).toFixed(4)`, which made the module docstring above false
 * and — worse — rendered the server's `0.000040` as `$0.0000`. Every view goes
 * to trouble to distinguish "nothing recorded" (an em dash) from real spend, and
 * `$0.0000` reads as exactly zero, so the one number that proves an agent did
 * work was displayed as proof that it did none.
 *
 * Trailing zeros are dropped past two places, so ordinary amounts stay readable
 * (`$1.2345`, `$5.00`) while a sub-cent one keeps every digit it was sent
 * (`$0.00004`). Six decimals is what the API emits; more than that is not a
 * number we were given.
 */
export function money(value: string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const [whole = "0", frac = ""] = value.split(".");
  const significant = frac.slice(0, 6).replace(/0+$/, "");
  return `$${whole}.${significant.padEnd(2, "0")}`;
}
